import * as React from "react";
import { Icon, type IconName } from "../icon/Icon";

/**
 * Single-line text input with label above, helper/error text below and optional icons.
 * Ported from the Wanted Design System bundle (components/forms/TextField.jsx).
 * Tellvue additions: helper text is linked with aria-describedby, errors set aria-invalid,
 * `trailing` slot for an inline action, and React 19 `ref` passthrough.
 */
export interface TextFieldProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: string;
  placeholder?: string;
  helperText?: React.ReactNode;
  error?: boolean;
  disabled?: boolean;
  /** @default "large" */
  size?: "large" | "medium";
  iconLeft?: IconName;
  iconRight?: IconName;
  trailing?: React.ReactNode;
  ref?: React.Ref<HTMLInputElement>;
}

const SIZES = {
  large: { height: 48, font: "var(--font-size-body-1)", lh: "var(--line-height-body-1)", pad: 16 },
  medium: { height: 40, font: "var(--font-size-body-2)", lh: "var(--line-height-body-2)", pad: 14 },
} as const;

export function TextField({
  label,
  value,
  placeholder,
  helperText,
  error = false,
  disabled = false,
  size = "large",
  iconLeft,
  iconRight,
  trailing,
  type = "text",
  style,
  onFocus,
  onBlur,
  ...rest
}: TextFieldProps) {
  const s = SIZES[size] ?? SIZES.large;
  const [focused, setFocused] = React.useState(false);
  const helperId = React.useId();
  const borderColor = error
    ? "var(--status-negative)"
    : focused
      ? "var(--primary-normal)"
      : "var(--line-normal-normal)";
  return (
    <label
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        minWidth: 0,
        fontFamily: "var(--font-sans)",
        ...style,
      }}
    >
      {label && (
        <span
          style={{
            fontSize: "var(--font-size-label-1)",
            lineHeight: "var(--line-height-label-1)",
            fontWeight: 600,
            color: "var(--label-normal)",
          }}
        >
          {label}
        </span>
      )}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          height: s.height,
          padding: `0 ${trailing ? 6 : s.pad}px 0 ${s.pad}px`,
          borderRadius: "var(--radius-md)",
          background: disabled
            ? "var(--background-normal-alternative)"
            : "var(--background-normal-normal)",
          boxShadow: `inset 0 0 0 ${focused && !error ? 1.5 : 1}px ${borderColor}`,
          transition: "box-shadow var(--duration-fast) var(--ease-standard)",
          opacity: disabled ? 0.6 : 1,
        }}
      >
        {iconLeft && (
          <Icon name={iconLeft} size={20} style={{ color: "var(--label-neutral)", flex: "none" }} />
        )}
        <input
          type={type}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          aria-invalid={error || undefined}
          aria-describedby={helperText ? helperId : undefined}
          onFocus={(e) => {
            setFocused(true);
            onFocus?.(e);
          }}
          onBlur={(e) => {
            setFocused(false);
            onBlur?.(e);
          }}
          style={{
            flex: 1,
            minWidth: 0,
            height: "100%",
            border: "none",
            outline: "none",
            background: "transparent",
            fontFamily: "var(--font-sans)",
            fontSize: s.font,
            lineHeight: s.lh,
            color: "var(--label-normal)",
          }}
          {...rest}
        />
        {iconRight && (
          <Icon
            name={iconRight}
            size={20}
            style={{ color: "var(--label-neutral)", flex: "none" }}
          />
        )}
        {trailing}
      </div>
      {helperText && (
        <span
          id={helperId}
          style={{
            fontSize: "var(--font-size-label-2)",
            lineHeight: "var(--line-height-label-2)",
            color: error ? "var(--status-negative)" : "var(--label-neutral)",
          }}
        >
          {helperText}
        </span>
      )}
    </label>
  );
}

export default TextField;
